import { Badge, Button, Text } from '@libs/components'
import { useTheme } from '@libs/theme'
import { SecureStore } from '@libs/utils'
import { isMobile, isWeb } from '@libs/utils/src/screenLayout'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import {
  Image,
  Linking,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native'
import { formatIntakeDate } from '@libs/utils/src/dateformat'
import { MyApplicationMobile } from './index.mobile'
import { badgeColor, excludedDurationBrands } from '../../constants'
import { formattedStatus } from '../../utils/formattedStatus'
import ConfirmationModal from '../modal/ConfirmationModal'
import { getOAPUrl } from '../../utils/getOAPUrl'

export const MyApplication = ({ data, style }) => {
  const isDesktop = isWeb(useWindowDimensions().width)
  const [brandLogoUrl, setBrandLogoUrl] = useState('')
  const { colors } = useTheme()
  const navigation = useNavigation()
  const isMobileScreen = isMobile(useWindowDimensions().width)
  const [showRedirectModal, setShowRedirectModal] = useState(false)

  const isDraft = data?.ApplicationStatus?.category === 'Drafts'
  const isConsentDraft = isDraft

  // Get OAP login URL for any brand using existing getOAPUrl utility
  const getOAPLoginUrl = () => {
    const baseUrl = getOAPUrl(data)
    return baseUrl ? `${baseUrl}/login` : ''
  }

  // Show modal for ALL draft applications
  const shouldShowModal = isDraft

  const handleNavigation = () => {
    // For ALL draft applications - show modal with link to external OAP
    if (shouldShowModal) {
      setShowRedirectModal(true)
      return // Prevent navigation to iframe
    }

    // For non-draft applications - navigate to view details (old behavior)
    navigation.navigate('view-application-details', {
      id: data.Id,
      isActive:
        data?.ActionRequiredCount > 0 ? 'ACTION_REQUIRED' : 'INFORMATION',
      isDashboard: true,
    })
  }

  // Handler for opening OAP login in new tab
  const handleOpenOAPLogin = () => {
    try {
      const loginUrl = getOAPLoginUrl()

      if (loginUrl) {
        // Use window.open for web, Linking for mobile
        if (typeof window !== 'undefined' && window.open) {
          const newWindow = window.open(
            loginUrl,
            '_blank',
            'noopener,noreferrer',
          )
          if (!newWindow) {
            console.warn('Pop-up blocked by the browser')
          }
        } else {
          Linking.openURL(loginUrl).catch((err) =>
            console.error('Failed to open URL:', err),
          )
        }
      } else {
        console.error('OAP Login URL is empty, cannot open new tab')
      }
    } catch (error) {
      console.error('Failed to open OAP login URL:', error)
    } finally {
      setShowRedirectModal(false)
    }
  }

  const isFocused = useIsFocused()

  const shouldRenderProcessingCard =
    data?.ApplicationStatus?.status === 'Drafts' &&
    data?.isSubmittedFromAppHero === true

  const duration = data?.OpportunityLineItems?.records[0]?.Product2?.Duration__c
  let opportunitiesDetails = isDraft
    ? [
        {
          label: t('APPLICATION_DETAIL.INTAKE'),
          value: data.IntakeDate ? formatIntakeDate(data.IntakeDate) : '-',
          separate: false,
        },
      ]
    : [
        {
          label: t('APPLICATION_DETAIL.INTAKE'),
          value: data.IntakeDate ? formatIntakeDate(data.IntakeDate) : '-',
          separate: false,
        },
        {
          value: duration || '-',
          label: t('APPLICATION_DETAIL.DURATION'),
          separate: false,
        },
        {
          label: t('APPLICATION_DETAIL.LOCATION'),
          value:
            data?.Location__c ??
            data?.OpportunityLineItems?.records?.[0]?.Location__c ??
            '-',
          separate: false,
        },
      ]

  if (excludedDurationBrands.includes(data.BusinessUnitFilter__c)) {
    opportunitiesDetails = opportunitiesDetails.filter(
      (item) => item.label !== t('APPLICATION_DETAIL.DURATION'),
    )
  }

  useEffect(() => {
    if (!isFocused) return
    ;(async () => {
      const configData = await SecureStore.getItemAsync('config')
      const config = JSON.parse(configData)?.config
      setBrandLogoUrl(config?.brandLogoUrl)
    })()
  }, [isFocused])

  const programName = data?.ProgrammeName__c
    ? `${data?.ProgrammeName__c}${
        data?.Delivery_Mode__c ? `, ${data?.Delivery_Mode__c}` : ''
      }`
    : ''

  const redirectModalContent = (
    <View style={styles.confirmationContent}>
      <Text
        style={styles.confirmationMessage}
        variant="heading6"
        color={colors.textPrimary}
      >
        We are making a few improvements, meanwhile please{' '}
        <Text
          onPress={handleOpenOAPLogin}
          style={styles.confirmationLink}
          variant="heading6"
          color={colors.buttonPrimary}
        >
          Click here
        </Text>{' '}
        to complete your application.
      </Text>
    </View>
  )

  // Render the processing card design when conditions are met
  if (shouldRenderProcessingCard) {
    return (
      <View
        style={[
          styles.mainContainer,
          { backgroundColor: '#DDE0EE', padding: isMobileScreen ? 24 : 30 },
        ]}
      >
        <View
          style={{
            flexDirection: isMobileScreen ? 'column' : 'row',
            alignItems: isMobileScreen ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            paddingBottom: 8,
            flexWrap: 'wrap',
          }}
        >
          <Image
            source={`${brandLogoUrl}/${data.BusinessUnitFilter__c?.replaceAll(
              ' ',
              '_',
            )}.png`}
            style={{
              height: 55,
              width: 126,
            }}
            alt={data?.brand}
            resizeMode="contain"
          />
          <View
            style={{
              flex: 1,
              alignSelf: 'center',
            }}
          >
            <Text
              style={{
                fontWeight: 700,
                fontSize: 16,
                color: colors.cardTitleColor,
                paddingRight: 24,
              }}
            >
              {programName || data?.Name?.split('_')[0]}
            </Text>
          </View>
        </View>

        {/* Processing Note Section */}
        <View
          style={{
            backgroundColor: '#F5F5F5',
            borderRadius: 8,
            paddingHorizontal: isMobileScreen ? 16 : 32,
            paddingVertical: isMobileScreen ? 12 : 16,
            flexDirection: 'row',
            alignItems: 'flex-start',
            marginTop: !isMobileScreen ? 0 : 12,
          }}
        >
          <View
            style={{
              backgroundColor: '#FF8C00',
              borderRadius: 20,
              width: 40,
              height: 40,
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: 12,
            }}
          >
            <svg
              width="42"
              height="43"
              viewBox="0 0 42 43"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="21" cy="21.2656" r="21" fill="#D4691B" />
              <path
                d="M23 11.2656H15C14.4696 11.2656 13.9609 11.4763 13.5858 11.8514C13.2107 12.2265 13 12.7352 13 13.2656V29.2656C13 29.7961 13.2107 30.3048 13.5858 30.6798C13.9609 31.0549 14.4696 31.2656 15 31.2656H27C27.5304 31.2656 28.0391 31.0549 28.4142 30.6798C28.7893 30.3048 29 29.7961 29 29.2656V17.2656L23 11.2656Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M23 11.2656V17.2656H29"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M25 22.2656H17"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M25 26.2656H17"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M19 18.2656H18H17"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </View>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontWeight: 700,
                fontSize: 16,
                color: '#D4691B',
                marginBottom: 4,
              }}
            >
              Note:
            </Text>
            <Text
              style={{
                fontSize: 14,
                color: '#030229',
                lineHeight: 20,
                fontWeight: 400,
              }}
            >
              Your Application is processing right now. Please refresh after a
              short while to see application details!
            </Text>
          </View>
        </View>
      </View>
    )
  }

  if (isDesktop) {
    if (isConsentDraft) {
      return (
        <View style={[styles.mainContainer, style]}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: 8,
              flexWrap: 'nowrap',
              gap: 16,
            }}
          >
            <Image
              source={`${brandLogoUrl}/${data.BusinessUnitFilter__c?.replaceAll(
                ' ',
                '_',
              )}.png`}
              style={{
                height: 55,
                width: 126,
              }}
              alt={data?.brand}
              resizeMode="contain"
            />
            <View
              style={{
                justifyContent: 'flex-start',
                flex: 1,
                alignSelf: 'baseline',
              }}
            >
              <Text
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: colors.cardTitleColor,
                  paddingRight: 20,
                }}
              >
                {programName || data?.Name?.split('_')[0]}
              </Text>

              <View
                style={{
                  flexDirection: 'row',
                  marginTop: 10,
                  gap: 4,
                  flexWrap: 'wrap',
                }}
              >
                {opportunitiesDetails?.slice(0, 3).map((item, index) => (
                  <View
                    key={index}
                    style={{ flexDirection: 'row', alignItems: 'center' }}
                  >
                    <Text variant="display4" color={colors.textSecondary}>
                      <Text variant="display4" color={colors.onNeutral}>
                        {item.label}:{' '}
                      </Text>
                      {item.value}
                    </Text>
                    {opportunitiesDetails.length - 1 !== index && (
                      <View style={styles.divider(colors)} />
                    )}
                  </View>
                ))}
              </View>
            </View>

            <View
              style={{
                justifyContent: 'flex-start',
                alignSelf: 'baseline',
                flexGrow: 0,
              }}
            >
              <Badge
                style={[
                  badgeColor[data?.ApplicationStatus?.status.split(' ')[0]] ||
                    badgeColor[data?.ApplicationStatus?.status.split('/')[0]],
                ]}
                textVariant="display4"
              >
                Status : {formattedStatus(data?.ApplicationStatus?.status)}
              </Badge>
            </View>
          </View>

          <View
            style={{
              marginTop: 18,
              flexDirection: 'row',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              label="Complete Application"
              onPress={handleNavigation}
              appearance="filled"
              buttonColor={colors.primaryVariant6}
              labelVariant="display5"
              labelStyle={{
                color: '#fff',
                fontWeight: '700',
                textTransform: 'uppercase',
              }}
              buttonStyle={{
                minWidth: 220,
                paddingHorizontal: 28,
                paddingVertical: 12,
              }}
            />
          </View>

          {/* Confirmation Modal for Draft Application Redirects */}
          {showRedirectModal && (
            <ConfirmationModal
              visible={showRedirectModal}
              onClose={() => setShowRedirectModal(false)}
              showCloseIcon
              showButtons={false}
            >
              {redirectModalContent}
            </ConfirmationModal>
          )}
        </View>
      )
    }

    return (
      <View style={[styles.mainContainer, style]}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: 8,
            flexWrap: !isDesktop ? 'wrap' : 'nowrap',
          }}
        >
          <Image
            source={`${brandLogoUrl}/${data.BusinessUnitFilter__c?.replaceAll(
              ' ',
              '_',
            )}.png`}
            style={{
              height: 55,
              width: 126,
            }}
            alt={data?.brand}
            resizeMode="contain"
          />
          {isDesktop ? (
            <View
              style={{
                justifyContent: 'flex-start',
                flex: 1,
                alignSelf: 'baseline',
              }}
            >
              <Text
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: colors.cardTitleColor,
                  paddingRight: 20,
                }}
              >
                {programName || data?.Name?.split('_')[0]}
              </Text>

              <View
                style={{
                  flexDirection: 'row',
                  marginTop: 10,
                  gap: 4,
                  flexWrap: 'wrap',
                }}
              >
                {opportunitiesDetails?.slice(0, 3).map((item, index) => (
                  <View
                    key={index}
                    style={{ flexDirection: 'row', alignItems: 'center' }}
                  >
                    <Text variant="display4" color={colors.textSecondary}>
                      <Text variant="display4" color={colors.onNeutral}>
                        {item.label}:{' '}
                      </Text>
                      {item.value}
                    </Text>
                    {opportunitiesDetails.length - 1 !== index && (
                      <View style={styles.divider(colors)} />
                    )}
                  </View>
                ))}
              </View>
            </View>
          ) : null}

          <View
            style={{
              justifyContent: 'flex-start',
              alignSelf: isDesktop ? 'baseline' : 'center',
              flexGrow: 0,
            }}
          >
            <Badge
              style={[
                badgeColor[data?.ApplicationStatus?.status.split(' ')[0]] ||
                  badgeColor[data?.ApplicationStatus?.status.split('/')[0]],
              ]}
              textVariant="display4"
            >
              Status : {formattedStatus(data?.ApplicationStatus?.status)}
            </Badge>
          </View>
        </View>

        {isDraft ? (
          <View
            style={{
              borderWidth: 0.5,
              borderColor: colors.divider,
              borderStyle: 'solid',
              marginVertical: 16,
            }}
          />
        ) : null}

        {isDraft ? (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 17,
            }}
          >
            <Button
              onPress={() =>
                navigation.navigate('new-oap-application', {
                  id: data.Id,
                  mode: 'update',
                })
              }
              appearance="filled"
              buttonColor={colors.buttonPrimary}
              label={t('BUTTON.COMPLETE_APPLICATION')}
              labelVariant="display5"
              labelStyle={{ color: colors.white, fontWeight: '500' }}
              buttonStyle={styles.buttonStyleFilled(isDesktop)}
            />
          </View>
        ) : null}
      </View>
    )
  }
  return (
    <>
      <MyApplicationMobile
        data={data}
        style={style}
        shouldRenderProcessingCard={shouldRenderProcessingCard}
        isMobileScreen={isMobileScreen}
        handleNavigation={handleNavigation}
      />
      {/* Confirmation Modal for Draft Application Redirects */}
      {showRedirectModal && (
        <ConfirmationModal
          visible={showRedirectModal}
          onClose={() => setShowRedirectModal(false)}
          showCloseIcon
          showButtons={false}
        >
          {redirectModalContent}
        </ConfirmationModal>
      )}
    </>
  )
}

const styles = StyleSheet.create({
  mainContainer: {
    borderRadius: 10,
    backgroundColor: '#fff',
    paddingHorizontal: 30,
    paddingVertical: 30,
  },
  data: () => ({
    flexDirection: window.innerWidth >= 376 ? 'row' : 'column',
    flexWrap: 'wrap',
    gap: window.innerWidth <= 376 ? 6 : 0,
  }),
  divider: (colors) => ({
    backgroundColor: colors.onNeutral,
    width: 1,
    height: '100%',
    marginHorizontal: 15,
  }),
  buttonStyleOutlined: (colors, isDesktop) => ({
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: isDesktop ? 40 : 20,
    paddingVertical: 10,
    borderColor: colors.primaryContainer,
  }),
  buttonStyleFilled: (isDesktop) => ({
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: isDesktop ? 40 : 20,
    paddingVertical: 10,
  }),
  confirmationContent: {
    marginTop: 12,
    paddingHorizontal: 8,
    paddingBottom: 4,
  },
  confirmationMessage: {
    textAlign: 'center',
    lineHeight: 24,
  },
  confirmationLink: {
    textDecorationLine: 'underline',
    fontWeight: '700',
    cursor: 'pointer',
  },
})
